// Referral codes, shared by the browser and the API (no server imports here).
//
// A creator shares `?ref=<code>` on any Jammable URL, or says the code in a
// video and the visitor types it at signup. Either way the code waits in the
// `jam_ref` cookie until /api/auth/register reads it and records who referred
// the new account. This replaced attribution by Tolt's own cookie, which was
// lost whenever someone clicked on one device and signed up on another.
//
// Any of LINK_PARAMS works the same as `?ref=`: creators pick the one that
// reads best to their audience (/earn offers LINK_STYLES), and Tolt-era links
// like `?via=<slug>` in old bios keep counting.

export const REFERRAL_COOKIE = "jam_ref";
// Last click wins within this window.
export const REFERRAL_COOKIE_DAYS = 30;

export type ReferralSource = "link" | "typed";

// Tolt slugs are lowercase letters, digits and hyphens; allow underscores too.
const CODE_RE = /^[a-z0-9][a-z0-9_-]{2,29}$/;

export function normalizeReferralCode(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const code = raw.trim().toLowerCase();
  return CODE_RE.test(code) ? code : null;
}

// The styles /earn offers, `ref` first (the default).
export const LINK_STYLES = ["ref", "via", "invite", "friend"] as const;
export type LinkStyle = (typeof LINK_STYLES)[number];

// Read in this order; aff, tolt and fpr are only for old Tolt links.
const LINK_PARAMS = [...LINK_STYLES, "aff", "tolt", "fpr"];

// The referral code in a page's query string, if any.
export function referralCodeFromSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  for (const name of LINK_PARAMS) {
    const code = normalizeReferralCode(params.get(name));
    if (code) return code;
  }
  return null;
}

// Cookie value: "<source>:<code>", e.g. "link:hello".
export function encodeReferralCookie(
  code: string,
  source: ReferralSource,
): string {
  return `${source}:${code}`;
}

export function decodeReferralCookie(
  value: unknown,
): { code: string; source: ReferralSource } | null {
  if (typeof value !== "string") return null;
  const [source, raw] = decodeURIComponent(value).split(":");
  const code = normalizeReferralCode(raw);
  if (!code || (source !== "link" && source !== "typed")) return null;
  return { code, source };
}

export function setReferralCookie(code: string, source: ReferralSource) {
  if (typeof document === "undefined") return;
  const maxAge = REFERRAL_COOKIE_DAYS * 24 * 3600;
  document.cookie = `${REFERRAL_COOKIE}=${encodeURIComponent(
    encodeReferralCookie(code, source),
  )}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

export function clearReferralCookie() {
  if (typeof document === "undefined") return;
  document.cookie = `${REFERRAL_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}

export function readReferralCookie() {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${REFERRAL_COOKIE}=`));
  return match
    ? decodeReferralCookie(match.slice(REFERRAL_COOKIE.length + 1))
    : null;
}

export function referralLink(
  code: string,
  path = "/",
  style: LinkStyle = "ref",
) {
  return `https://www.jammable.com${path}?${style}=${code}`;
}

// One visit per browser per code per UTC day, for the graphs on /earn
// (pages/api/referral/visit.ts). Best effort: storage or network failing
// just means the visit isn't counted.
export function countReferralVisit(code: string) {
  if (typeof window === "undefined") return;
  const key = `jam_ref_visit:${code}:${new Date().toISOString().slice(0, 10)}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
  } catch {
    return;
  }
  fetch("/api/referral/visit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
    keepalive: true,
  }).catch(() => {});
}
